const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ValmontiCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-loaded', 'data-url'];

  #eventController;

  #requestController;

  #requestGeneration = 0;

  #loadScheduled = false;

  connectedCallback() {
    this.#eventController?.abort();
    this.#eventController = new AbortController();
    const { signal } = this.#eventController;
    this.addEventListener('change', this.#handleVariantChange, { signal });
    this.addEventListener('click', this.#handleCarouselClick, { signal });

    this.#scheduleLoad();
  }

  disconnectedCallback() {
    this.#eventController?.abort();
    this.#cancelLoad();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;

    if (name === 'data-url') {
      this.#cancelLoad();
      if (this.dataset.loaded === 'true') this.dataset.loaded = 'false';
    }

    if (this.dataset.loaded !== 'true') this.#scheduleLoad();
  }

  #handleVariantChange = (event) => {
    const select = event.target instanceof HTMLSelectElement ? event.target : null;
    if (!select?.matches('[data-valmonti-cart-recommendation-variant]')) return;

    const card = select.closest('[data-valmonti-cart-recommendation-card]');
    const selectedOption = select.selectedOptions[0];
    if (!card || !selectedOption) return;

    const variantInput = card.querySelector('[data-valmonti-cart-recommendation-variant-id]');
    const currentPrice = card.querySelector('[data-valmonti-cart-recommendation-current]');
    const currentPriceLabel = card.querySelector('[data-valmonti-cart-recommendation-current-label]');
    const comparePrice = card.querySelector('[data-valmonti-cart-recommendation-compare]');

    if (variantInput instanceof HTMLInputElement) variantInput.value = selectedOption.value;
    if (currentPrice) currentPrice.textContent = selectedOption.dataset.price || '';
    if (currentPriceLabel) {
      currentPriceLabel.textContent = selectedOption.dataset.compare
        ? currentPriceLabel.dataset.saleLabel || ''
        : currentPriceLabel.dataset.priceLabel || '';
    }
    if (comparePrice) {
      comparePrice.textContent = selectedOption.dataset.compare || '';
      comparePrice.toggleAttribute('hidden', !selectedOption.dataset.compare);
    }
  };

  #handleCarouselClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button) return;

    const direction = button.hasAttribute('data-valmonti-cart-recommendations-previous')
      ? -1
      : button.hasAttribute('data-valmonti-cart-recommendations-next')
        ? 1
        : 0;
    if (!direction) return;

    const track = this.querySelector('[data-valmonti-cart-recommendations-track]');
    if (!(track instanceof HTMLElement)) return;

    track.scrollBy({
      left: direction * Math.max(220, track.clientWidth * 0.85),
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  };

  #cancelLoad() {
    this.#requestGeneration += 1;
    this.#requestController?.abort();
    this.#requestController = undefined;
    this.removeAttribute('aria-busy');
  }

  #scheduleLoad() {
    if (this.#loadScheduled || this.dataset.loaded === 'true') return;

    this.#loadScheduled = true;
    queueMicrotask(() => {
      this.#loadScheduled = false;
      if (this.isConnected && this.dataset.loaded !== 'true') this.#load();
    });
  }

  async #load() {
    const requestUrl = this.dataset.url;
    if (!requestUrl) {
      this.hidden = true;
      return;
    }

    this.#requestController?.abort();
    this.#requestController = new AbortController();
    const requestController = this.#requestController;
    const requestGeneration = ++this.#requestGeneration;

    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: requestController.signal,
      });
      if (!response.ok) throw new Error(`Recommendations request failed with ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const loaded = responseDocument.querySelector('valmonti-cart-recommendations[data-loaded="true"]');
      if (
        !this.isConnected ||
        requestGeneration !== this.#requestGeneration ||
        requestUrl !== this.dataset.url
      ) {
        return;
      }

      if (!loaded || loaded.hidden || !loaded.querySelector('[data-valmonti-cart-recommendation-card]')) {
        this.dataset.loaded = 'true';
        this.hidden = true;
        return;
      }

      this.replaceChildren(...Array.from(loaded.childNodes, (node) => document.importNode(node, true)));
      this.dataset.loaded = 'true';
      this.hidden = false;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[valmonti-cart] Recommendations unavailable:', error);
        this.hidden = true;
      }
    } finally {
      if (requestGeneration === this.#requestGeneration) {
        this.#requestController = undefined;
        this.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('valmonti-cart-recommendations')) {
  customElements.define('valmonti-cart-recommendations', ValmontiCartRecommendations);
}

const easternParts = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function getEasternParts(date) {
  return Object.fromEntries(
    easternParts
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)])
  );
}

function getEasternOffset(date) {
  const parts = getEasternParts(date);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - date.getTime();
}

function getNextEasternMidnight(now) {
  const current = getEasternParts(now);
  const wallClockTarget = Date.UTC(current.year, current.month - 1, current.day + 1, 0, 0, 0);
  let target = wallClockTarget - getEasternOffset(new Date(wallClockTarget));
  target = wallClockTarget - getEasternOffset(new Date(target));
  return target;
}

class MasonSaleCountdown extends HTMLElement {
  #interval;

  connectedCallback() {
    this.#update();
    this.#interval = window.setInterval(() => this.#update(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
  }

  #update() {
    const now = new Date();
    const secondsRemaining = Math.max(0, Math.floor((getNextEasternMidnight(now) - now.getTime()) / 1000));
    const hours = Math.floor(secondsRemaining / 3600);
    const minutes = Math.floor((secondsRemaining % 3600) / 60);
    const seconds = secondsRemaining % 60;

    const values = {
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    };

    Object.entries(values).forEach(([unit, value]) => {
      const output = this.querySelector(`[data-mason-countdown-${unit}]`);
      if (output) output.textContent = value;
    });

    const timer = this.querySelector('[role="timer"]');
    timer?.setAttribute(
      'aria-label',
      `${hours} hours, ${minutes} minutes, and ${seconds} seconds until the Moving Sale ends at midnight Eastern Time`
    );
  }
}

if (!customElements.get('mason-sale-countdown')) {
  customElements.define('mason-sale-countdown', MasonSaleCountdown);
}
